
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ServerTime struct
 */
class ServerTime {
  /**
   * Initialize struct
   * @param {!Date=} time
   * @constructor
   */
  constructor (time = new Date(0)) {
    this.Time = time
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ServerTime}
   * @param {!ServerTime} other Other struct
   * @returns {!ServerTime} This struct
   */
  copy (other) {
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ServerTime}
   * @returns {!ServerTime} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ServerTimeModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ServerTimeModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ServerTime}
   * @param {!ServerTime} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ServerTime)) {
      throw new TypeError('Instance of ServerTime is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ServerTime}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ServerTime.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ServerTime} other Object value
   * @returns {!ServerTime} Created struct
   */
  static fromObject (other) {
    return new ServerTime().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ServerTime}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ServerTime.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ServerTime}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 15
  }
}

export { ServerTime };

/**
 * Fast Binary Encoding ServerTime field model
 */
class FieldModelServerTime extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Time = new fbe.FieldModelTimestamp(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelServerTime}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelServerTime}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Time.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelServerTime}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Time.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelServerTime}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelServerTime.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelServerTime}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 15
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelServerTime}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelServerTime}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelServerTime}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelServerTime}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelServerTime}
   * @param {!ServerTime} fbeValue Default value, defaults is new ServerTime()
   * @returns {!ServerTime} ServerTime value
   */
  get (fbeValue = new ServerTime()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelServerTime}
   * @param {!ServerTime} fbeValue ServerTime value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelServerTime}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelServerTime}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelServerTime}
   * @param {!ServerTime} fbeValue ServerTime value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelServerTime}
   * @param {ServerTime} fbeValue ServerTime value
   */
  setFields (fbeValue) {
    this.Time.set(fbeValue.Time)
  }
}

export { FieldModelServerTime };

/**
 * Fast Binary Encoding ServerTime model
 */
class ServerTimeModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelServerTime(this.buffer, 4)
  }

  /**
   * Get the ServerTime model
   * @this {!ServerTimeModel}
   * @returns {!FieldModelServerTime} model ServerTime model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ServerTimeModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ServerTimeModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ServerTimeModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ServerTimeModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelServerTime.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ServerTimeModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ServerTimeModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ServerTimeModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ServerTimeModel}
   * @param {!ServerTime} value ServerTime value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ServerTimeModel}
   * @param {!ServerTime} value ServerTime value, defaults is new ServerTime()
   * @return {!object} Deserialized ServerTime value and its size
   */
  deserialize (value = new ServerTime()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ServerTime(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ServerTime(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ServerTimeModel}
   * @param {!number} prev Previous ServerTime model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ServerTimeModel };
